// src/features/settings/ProfileSettings.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Avatar, Grid, CircularProgress, Alert, IconButton, styled } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { updateUserProfile, clearAuthStatus } from '../../features/auth/authSlice';
import { User } from '../../types';
import ChangeEmailDialog from './ChangeEmailDialog';
import UpdateAvatarDialog from './components/UpdateAvatarDialog'; // Import the new dialog
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next'; // Import i18n hook

interface ProfileSettingsProps {
    user: User;
}

const AvatarContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: 80,
    height: 80,
    '&:hover .camera-overlay': {
        opacity: 1,
    },
}));

const CameraOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transition: theme.transitions.create('opacity'),
}));


const ProfileSettings: React.FC<ProfileSettingsProps> = ({ user }) => {
    const { t } = useTranslation(); // Initialize i18n translation
    const dispatch = useAppDispatch();
    const { status, error, message } = useAppSelector(state => state.auth);

    const [name, setName] = useState(user.name);
    const [nameError, setNameError] = useState<string | null>(null); // State for name field error
    const [isEmailChangeOpen, setEmailChangeOpen] = useState(false);
    const [isAvatarDialogOpen, setAvatarDialogOpen] = useState(false); // State for new dialog

    // Clear auth status on component mount
    useEffect(() => {
        // This action needs to be defined in your slice
        // dispatch(clearAuthStatus()); 
    }, [dispatch]);

    // Update local state if user object changes from Redux
    useEffect(() => {
        setName(user.name);
    }, [user.name]);

    // Validate name field on change
    useEffect(() => {
        if (!name.trim()) {
            setNameError(t('name_required')); // "Name is required"
        } else if (name.length > 40) {
            setNameError(t('name_too_long')); // "Name must not exceed 40 characters"
        } else {
            setNameError(null);
        }
    }, [name, t]);

    const handleProfileUpdate = (e: React.FormEvent) => {
        e.preventDefault();
        if (name !== user.name && !nameError) {
            dispatch(updateUserProfile({ name }));
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom >{t('profile_settings')}</Typography>
            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <Box component="form" onSubmit={handleProfileUpdate} sx={{ py: 3 }}>
                <Grid container spacing={4} direction="column">
                    <Grid item {...{} as any}>
                        {/* Profile Photo Section */}
                        <Typography variant="h6" gutterBottom>{t('profile_photo')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('profile_photo_desc')}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <AvatarContainer onClick={() => setAvatarDialogOpen(true)}>
                                <Avatar src={user.avatarUrl} sx={{ width: 80, height: 80, fontSize: '2.5rem' }}>
                                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                </Avatar>
                                <CameraOverlay className="camera-overlay">
                                    <PhotoCamera />
                                </CameraOverlay>
                            </AvatarContainer>
                            <Button
                                variant="outlined"
                                onClick={() => setAvatarDialogOpen(true)}
                                sx={{
                                    // Apply LoginPage button styles
                                    color: 'inverse.main',
                                    borderColor: 'inverse.main',
                                    '&:hover': {
                                        borderColor: 'inverse.hover.main',
                                        backgroundColor: 'inverse.hover.main',
                                        color: 'inverse.contrastText',
                                    },
                                }}
                            >
                                {t('change')}
                            </Button>
                        </Box>
                        <Box sx={{ py: 1 }}/>
                    </Grid>

                    {/* User Information Section */}
                    <Grid item {...{} as any}>
                        <Typography variant="h6" gutterBottom>{t('user_info')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('user_info_desc')}
                        </Typography>
                    </Grid>

                    <Grid item {...{} as any}>
                        <TextField
                            required
                            fullWidth
                            label={t('full_name')}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={{ maxWidth: 400 }}
                            error={!!nameError}
                            helperText={nameError}
                            inputProps={{ maxLength: 40 }} // Set max length to 40
                        />
                    </Grid>

                    <Grid item {...{} as any}>
                        <TextField
                            fullWidth
                            label={t('email_address')}
                            value={user.email}
                            disabled
                            sx={{ maxWidth: 400 }}
                            InputProps={{
                                endAdornment: (
                                    <Button
                                        size="small"
                                        onClick={() => setEmailChangeOpen(true)}
                                        sx={{
                                            // Apply LoginPage button styles
                                            color: 'inverse.main',
                                            '&:hover': {
                                                backgroundColor: 'inverse.hover.main',
                                                color: 'inverse.contrastText',
                                            },
                                        }}
                                    >
                                        {t('change')}
                                    </Button>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item {...{} as any}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={status.user === 'loading' || name === user.name || !!nameError}
                            sx={{
                                // Apply LoginPage button styles
                                bgcolor: 'inverse.main',
                                color: 'inverse.contrastText',
                                '&:hover': {
                                    bgcolor: 'inverse.hover.main',
                                },
                            }}
                        >
                            {status.user === 'loading' ? <CircularProgress size={24} color="inherit" /> : t('save_changes')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Email Change Dialog */}
            <ChangeEmailDialog
                open={isEmailChangeOpen}
                onClose={() => setEmailChangeOpen(false)}
                currentEmail={user.email}
            />

            {/* NEW: Avatar Update Dialog */}
            {user && (
                <UpdateAvatarDialog
                    open={isAvatarDialogOpen}
                    onClose={() => setAvatarDialogOpen(false)}
                    user={user}
                />
            )}
        </Box>
    );
};

export default ProfileSettings;